/*
 * What the sign-in says, decided apart from the screen (docs/2026-10-19-sign-in.md): the greeting for
 * the visitor's own hour, and what one answer of `POST /api/auth/sign-in/email` means for the person.
 * Pure: the caller reads the clock and the response.
 */

/**
 * The greeting for an hour of the visitor's own clock, 0 to 23. The hour comes from the browser, in its
 * time zone, never from the server or a campaign: reps sign in from many zones (Dawid, 2026-10-19).
 * English has no greeting after midnight ("Good night" is a goodbye), so the evening runs until 05:00.
 */
export function greetingFor(hour: number): string {
  if (hour >= 5 && hour < 12) return "Good morning.";
  if (hour >= 12 && hour < 18) return "Good afternoon.";
  return "Good evening.";
}

export type SignInOutcome =
  | { readonly kind: "signed_in" }
  | { readonly kind: "rejected" }
  | { readonly kind: "limited"; readonly retryAfterMs: number }
  | { readonly kind: "unreachable" };

export type SignInFailure = Exclude<SignInOutcome, { readonly kind: "signed_in" }>;

/** Better Auth's sign-in allows 5 tries a minute per address (`src/auth.ts`); a wait it did not name is that minute. */
const DEFAULT_WAIT_MS = 60_000;

/**
 * One answer of the sign-in route, by status. 0 is the network failing. A wrong password, an unknown
 * email and an account that is not invited all read as one rejection, so the page never tells a
 * stranger which addresses have an account.
 */
export function signInOutcome(status: number, retryAfter: string | null): SignInOutcome {
  if (status >= 200 && status < 300) return { kind: "signed_in" };
  if (status === 429) {
    const seconds = retryAfter === null ? Number.NaN : Number(retryAfter);
    return {
      kind: "limited",
      retryAfterMs: Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : DEFAULT_WAIT_MS,
    };
  }
  if (status === 0 || status >= 500) return { kind: "unreachable" };
  return { kind: "rejected" };
}

/** What the page says under the password for a failure. */
export function failureMessage(failure: SignInFailure): string {
  switch (failure.kind) {
    case "rejected":
      return "That email and password don't match. Check both, or reset your password.";
    case "limited":
      return "Too many tries. Wait a minute, then try again.";
    case "unreachable":
      return "Could not reach LeadCue. Check your connection and try again.";
  }
}
